/**
 * KanbanBoard - Main Kanban board component
 */
import React, { useEffect, useState } from 'react';
import { DragDropContext, DropResult } from '@hello-pangea/dnd';
import { Task, TaskStatus } from '../../types/task';
import { KanbanColumn } from './KanbanColumn';
import { useTaskStore } from '../../stores/taskStore';
import { useProjectStore } from '../../stores/projectStore';

interface KanbanBoardProps {
  onTaskClick: (task: Task) => void;
}

export const KanbanBoard: React.FC<KanbanBoardProps> = ({ onTaskClick }) => {
  const { currentProject } = useProjectStore();
  const { tasks, fetchTasks, updateTaskStatus, loading } = useTaskStore();

  useEffect(() => {
    if (currentProject?.id) {
      fetchTasks(currentProject.id);
    }
  }, [currentProject?.id, fetchTasks]);

  const handleDragEnd = async (result: DropResult) => {
    const { destination, source, draggableId } = result;

    // Dropped outside the list
    if (!destination) {
      return;
    }

    // Dropped in the same position
    if (
      destination.droppableId === source.droppableId &&
      destination.index === source.index
    ) {
      return;
    }

    // Get task ID from draggableId (format: "task-123")
    const taskId = parseInt(draggableId.replace('task-', ''), 10);
    const newStatus = destination.droppableId as TaskStatus;

    // Update task status
    await updateTaskStatus(taskId, newStatus);
  };

  // Group tasks by status
  const tasksByStatus: Record<TaskStatus, Task[]> = {
    [TaskStatus.TODO]: tasks.filter((t) => t.status === TaskStatus.TODO),
    [TaskStatus.IN_PROGRESS]: tasks.filter((t) => t.status === TaskStatus.IN_PROGRESS),
    [TaskStatus.TESTING]: tasks.filter((t) => t.status === TaskStatus.TESTING),
    [TaskStatus.DONE]: tasks.filter((t) => t.status === TaskStatus.DONE),
  };

  if (loading && tasks.length === 0) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
        <div style={{ textAlign: 'center' }}>
          <p>Načítavam úlohy...</p>
        </div>
      </div>
    );
  }

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="kanban-board">
        <KanbanColumn
          status={TaskStatus.TODO}
          tasks={tasksByStatus[TaskStatus.TODO]}
          onTaskClick={onTaskClick}
        />
        <KanbanColumn
          status={TaskStatus.IN_PROGRESS}
          tasks={tasksByStatus[TaskStatus.IN_PROGRESS]}
          onTaskClick={onTaskClick}
        />
        <KanbanColumn
          status={TaskStatus.TESTING}
          tasks={tasksByStatus[TaskStatus.TESTING]}
          onTaskClick={onTaskClick}
        />
        <KanbanColumn
          status={TaskStatus.DONE}
          tasks={tasksByStatus[TaskStatus.DONE]}
          onTaskClick={onTaskClick}
        />
      </div>
    </DragDropContext>
  );
};

